import React, { useContext } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { LanguageContext } from "../../context/LanguageContext";
import "./VisualShowcase.css";

const labels = {
  en: {
    sectionTitle: "What I build",
    sectionSub: "From high-volume data and integrations to agents in production.",
    cards: [
      {
        title: "Agentic systems",
        body: "Cognitive orchestrators that split a hard question, route each part to the right source, and return an answer you can trace.",
        tags: ["Multi-agent", "Tool-calling", "Planning", "RAG", "Self-correction"],
        foot: "Live across 9+ enterprise tenants",
      },
      {
        title: "Semantic data modeling",
        body: "The layer that makes enterprise data readable by AI. Traits, relationships, quality checks, and the rules that stop a wrong query sounding confident.",
        tags: ["Ontologies", "Traits", "NL-to-SQL", "Data quality", "Constraints"],
        foot: "Schemas designed from the raw source",
      },
      {
        title: "LLMOps",
        body: "Tracing, evals, cost, and retries on every agent and every model call. Production is the product.",
        tags: ["Logfire", "Tracing", "Eval", "Cost", "E2E tests"],
        foot: "Quality measured, not assumed",
      },
      {
        title: "Integrations & MCP",
        body: "Agents that plug into the systems you already run. MCP servers, APIs, and connectors — with auth, scopes, and a clear contract for every tool.",
        tags: ["MCP", "APIs", "Connectors", "Auth", "Tool contracts"],
        foot: "The model reaches the system of record",
      },
      {
        title: "Workflow automation",
        body: "Multi-step jobs that used to be a brittle script: routing, retries, human handoff, and an audit trail when something fails.",
        tags: ["Orchestration", "Workflows", "Human-in-the-loop", "Retries", "Audit"],
        foot: "From a trigger to a finished job",
      },
      {
        title: "High-volume data",
        body: "Pipelines and serving layers that stay correct when the table is large and the schema is messy. Warehousing, quality checks, and the path from raw event to a query an agent can trust.",
        tags: ["Pipelines", "Warehousing", "BigQuery", "Data quality", "Scale"],
        foot: "Built for production volume",
      },
    ],
  },
  pt: {
    sectionTitle: "O que eu construo",
    sectionSub: "De dados em alto volume e integrações a agentes em produção.",
    cards: [
      {
        title: "Sistemas agênticos",
        body: "Orquestradores que quebram a pergunta, roteiam cada parte à fonte certa e devolvem uma resposta rastreável.",
        tags: ["Multi-agente", "Tool-calling", "Planejamento", "RAG", "Autocorreção"],
        foot: "Em operação em 9+ tenants empresariais",
      },
      {
        title: "Modelagem semântica",
        body: "A camada que torna o dado empresarial legível por IA. Traits, relações, qualidade e as regras que impedem uma query errada de parecer certa.",
        tags: ["Ontologias", "Traits", "NL-to-SQL", "Qualidade", "Restrições"],
        foot: "Schemas desenhados a partir da fonte",
      },
      {
        title: "LLMOps",
        body: "Tracing, evals, custo e retry em cada agente e cada chamada. Produção é o produto.",
        tags: ["Logfire", "Tracing", "Eval", "Custo", "Testes E2E"],
        foot: "Qualidade medida, não assumida",
      },
      {
        title: "Integrações e MCP",
        body: "Agentes que entram nos sistemas que você já opera. Servidores MCP, APIs e conectores — com auth, escopos e um contrato claro para cada ferramenta.",
        tags: ["MCP", "APIs", "Conectores", "Auth", "Contratos"],
        foot: "O modelo chega no sistema de registro",
      },
      {
        title: "Automação de fluxos",
        body: "Jobs de vários passos que antes eram um script frágil: roteamento, retry, handoff humano e trilha de auditoria quando falha.",
        tags: ["Orquestração", "Workflows", "Human-in-the-loop", "Retries", "Auditoria"],
        foot: "Do gatilho ao job concluído",
      },
      {
        title: "Dados em alto volume",
        body: "Pipelines e camadas de serving que continuam corretas quando a tabela é grande e o schema é bagunçado. Warehousing, qualidade e o caminho do evento cru até uma query em que o agente confia.",
        tags: ["Pipelines", "Warehousing", "BigQuery", "Qualidade", "Escala"],
        foot: "Feito para volume de produção",
      },
    ],
  },
};

const GlowCard = ({ card, delay }) => (
  <Box
    className="showcase-card-enter card-glow"
    style={{ animationDelay: delay }}
    sx={{
      minWidth: 0,
      background: "rgba(198, 164, 106, 0.05)",
      border: "1px solid rgba(198, 164, 106, 0.18)",
      borderRadius: 3,
      p: { xs: 3, md: 3.5 },
      display: "flex",
      flexDirection: "column",
      gap: 2,
      touchAction: "manipulation",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "@media (hover: hover)": {
        transition: "border-color 0.35s ease, box-shadow 0.35s ease, transform 0.35s ease",
        "&:hover": {
          borderColor: "rgba(198, 164, 106, 0.55)",
          boxShadow: "0 0 36px rgba(198, 164, 106, 0.16)",
          transform: "translateY(-6px)",
        },
      },
    }}
  >
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#c6a46a",
          boxShadow: "0 0 10px rgba(198,164,106,0.7)",
          flexShrink: 0,
        }}
      />
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#f3efe6" }}>
        {card.title}
      </Typography>
    </Box>

    <Typography variant="body2" sx={{ color: "rgba(243,239,230,0.64)", lineHeight: 1.7 }}>
      {card.body}
    </Typography>

    <Box display="flex" flexWrap="wrap" gap={0.75}>
      {card.tags.map((tag) => (
        <Chip
          key={tag}
          label={tag}
          size="small"
          sx={{
            fontSize: "0.68rem",
            height: 22,
            background: "rgba(198, 164, 106, 0.1)",
            color: "#e4d2a8",
            border: "1px solid rgba(198, 164, 106, 0.2)",
          }}
        />
      ))}
    </Box>

    <Box
      sx={{
        mt: "auto",
        pt: 2,
        borderTop: "1px solid rgba(198, 164, 106, 0.12)",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#c6a46a" }} />
      <Typography variant="caption" sx={{ color: "rgba(243,239,230,0.45)", fontSize: "0.72rem" }}>
        {card.foot}
      </Typography>
    </Box>
  </Box>
);

const VisualShowcase = () => {
  const { language } = useContext(LanguageContext);
  const t = labels[language] || labels.en;

  return (
    <Box
      component="section"
      aria-labelledby="build-heading"
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        py: { xs: 8, md: 10 },
        background:
          "radial-gradient(ellipse at 20% 50%, rgba(198,164,106,0.05) 0%, transparent 55%), #0a0a0a",
      }}
    >
      <Box className="page-width">
        <Box textAlign="center" mb={6}>
          <Typography id="build-heading" variant="h2" component="h2" sx={{ mb: 1.2, textTransform: "none", letterSpacing: -0.6, textAlign: "center" }}>
            {t.sectionTitle}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t.sectionSub}
          </Typography>
        </Box>

        <Box
          display="grid"
          gap={3}
          sx={{
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            alignItems: "stretch",
          }}
        >
          {t.cards.map((card, i) => (
            <GlowCard key={card.title} card={card} delay={`${0.1 + i * 0.12}s`} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default VisualShowcase;
